import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Compass, Orbit, Hash, Activity, ArrowRight, Check } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import CTABand from "@/components/CTABand";
import { Reveal } from "@/lib/motion";
import { SERVICES, IMAGES } from "@/lib/content";

const ICONS = { "scientific-vastu": Compass, astrology: Orbit, numerology: Hash, "energy-scanning": Activity };

export default function Services() {
  const location = useLocation();

  useEffect(() => {
    const sectionId = decodeURIComponent(location.hash.slice(1));
    if (!sectionId) return;

    document.getElementById(sectionId)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.hash]);

  return (
    <div className="relative z-10">
      <PageHeader
        overline="What We Offer"
        titleLines={["Four Pillars of", <span key="2" className="text-gold italic">Aligned Living</span>]}
        subtitle="A complete, science-led ecosystem to harmonise your spaces, decisions and energy."
        image={IMAGES.energy}
      />

      <section className="py-10 md:py-16">
        <div className="mx-auto max-w-7xl px-6 md:px-8 space-y-24 md:space-y-32">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.key];
            const reverse = i % 2 === 1;
            return (
              <div key={s.key} id={s.key} className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center scroll-mt-24" data-testid={`service-section-${s.key}`}>
                <div className={`lg:col-span-6 ${reverse ? "lg:order-2" : ""}`}>
                  <Reveal y={50}>
                    <div className="relative overflow-hidden rounded-[1.75rem] border border-white/10">
                      <img src={s.image} alt={s.title} className="w-full h-[420px] object-cover" />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#060A17]/70 to-transparent" />
                      <span className="absolute top-6 left-6 font-display text-7xl text-white/20">0{i + 1}</span>
                    </div>
                  </Reveal>
                </div>
                <div className={`lg:col-span-6 ${reverse ? "lg:order-1" : ""}`}>
                  <Reveal>
                    <span className="grid place-items-center h-14 w-14 rounded-2xl bg-primary-green/15 text-primary-green ring-1 ring-primary-green/30 mb-6"><Icon size={26} /></span>
                    <p className="overline text-gold mb-3">{s.tagline}</p>
                    <h2 className="font-display text-4xl md:text-5xl tracking-tight">{s.title}</h2>
                    <p className="mt-5 text-[#94A3B8] text-lg leading-relaxed">{s.desc}</p>
                    <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                      {s.items.map((it) => (
                        <li key={it} className="flex items-center gap-3 text-[#CBD5E1]">
                          <span className="grid place-items-center h-5 w-5 rounded-full bg-gold/15 text-gold shrink-0"><Check size={12} /></span>
                          {it}
                        </li>
                      ))}
                    </ul>
                    <Link to="/contact" data-testid={`service-cta-${s.key}`} className="mt-9 inline-flex items-center gap-2 rounded-full bg-primary-green px-6 py-3 text-white font-medium transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#059669]">
                      Enquire about {s.title} <ArrowRight size={16} />
                    </Link>
                  </Reveal>
                </div>
              </div>
            );
          })}
        </div>
      </section>
      <CTABand />
    </div>
  );
}
